import React, { useEffect, useRef, useState } from "react";
import { FilePdf, Image as ImageIcon, File as FileIcon } from "@phosphor-icons/react";
import api from "@/lib/api";

export const KAT_LABEL = { gutachten: "Gutachten", bussgeld: "Bußgeldbescheid", rechnung: "Rechnung", schriftverkehr: "Schriftverkehr", polizei: "Polizeibericht", vollmacht: "Vollmacht", abtretung: "Abtretung", sonstige: "Sonstiges" };
export const KAT_COLOR = { gutachten: "bg-[#1A2238]/10 text-[#1A2238]", bussgeld: "bg-[#D32F2F]/10 text-[#B91C1C]", rechnung: "bg-[#F59E0B]/10 text-[#B45309]", vollmacht: "bg-[#16A34A]/10 text-[#15803D]", abtretung: "bg-[#16A34A]/10 text-[#15803D]", schriftverkehr: "bg-[#0EA5E9]/10 text-[#0369A1]", polizei: "bg-[#6366F1]/10 text-[#4338CA]" };
export const STATUS = [
  { key: "neu", label: "Neu", dot: "#F59E0B" },
  { key: "geprueft", label: "Geprüft", dot: "#0EA5E9" },
  { key: "erledigt", label: "Erledigt", dot: "#16A34A" },
];
export const statusOf = (d) => d.dms_status || "neu";
export const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
export const fmtDate = (iso, time = true) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", ...(time ? { hour: "2-digit", minute: "2-digit" } : {}) }) : "—");
export const isPdf = (d) => d.content_type === "application/pdf" || /\.pdf$/i.test(d.original_filename || "");
export const isImg = (d) => (d.content_type || "").startsWith("image/") || /\.(jpe?g|png|gif|bmp|webp|heic|heif|tiff?)$/i.test(d.original_filename || "");

export function DocIcon({ d, size = 22 }) {
  const Icon = isPdf(d) ? FilePdf : isImg(d) ? ImageIcon : FileIcon;
  return <Icon size={size} weight="duotone" className={isPdf(d) ? "text-[#D32F2F]" : isImg(d) ? "text-[#0EA5E9]" : "text-[#6B7280]"} />;
}

const thumbCache = new Map();
const thumbFailed = new Set();

export function Thumb({ d, className = "" }) {
  const ref = useRef(null);
  const key = `${d.id}-${d.version_nr || 1}`;
  const [url, setUrl] = useState(thumbCache.get(key) || null);
  const [failed, setFailed] = useState(thumbFailed.has(key));

  useEffect(() => {
    if (url || failed) return;
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const io = new IntersectionObserver(async ([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      try {
        const r = await api.get(`/dokumente/${d.id}/thumbnail`, { responseType: "blob" });
        if (cancelled) return;
        const u = URL.createObjectURL(r.data);
        thumbCache.set(key, u);
        setUrl(u);
      } catch { thumbFailed.add(key); if (!cancelled) setFailed(true); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => { cancelled = true; io.disconnect(); };
  }, [d.id, key, url, failed]);

  return (
    <div ref={ref} className={`bg-[#F3F4F6] overflow-hidden flex items-center justify-center ${className}`} data-testid={`thumb-${d.id}`}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover object-top" draggable={false} /> : <DocIcon d={d} size={34} />}
    </div>
  );
}
